import {
  warmUpImmersiveARSupport
} from "./chunk-PM3UV6CK.mjs";
import {
  reportCaughtError
} from "./chunk-ETY2OVGU.mjs";

// src/utils/sanitizeHtml.ts
import DOMPurify from "isomorphic-dompurify";
var ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "b",
  "i",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "blockquote",
  "a",
  "code",
  "pre",
  "hr"
];
var ALLOWED_ATTR = ["href", "title", "target", "rel", "class"];
var ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;
var CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOWED_URI_REGEXP,
  // DOMPurify defaults these to true; the backend's sanitize-html config
  // strips data-*/aria-*, and this list must actually mirror it.
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false
};
var hookRegistered = false;
function ensureAnchorHook() {
  if (hookRegistered) return;
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    const el = node;
    if (el.tagName === "A") {
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer");
    }
  });
  hookRegistered = true;
}
function sanitizeHtml(html) {
  ensureAnchorHook();
  return DOMPurify.sanitize(html, CONFIG);
}

// src/components/PageModal.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef
} from "react";
import { createPortal } from "react-dom";
var PageModalContainerContext = createContext(null);
var PageModalContainerProvider = PageModalContainerContext.Provider;
var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
function PageModal({
  open,
  onBackdropClick,
  ariaLabel,
  children,
  align = "center"
}) {
  const container = useContext(PageModalContainerContext);
  const rootRef = useRef(null);
  const setRoot = useCallback((el) => {
    rootRef.current = el;
  }, []);
  useEffect(() => {
    if (!open || typeof document === "undefined" || !rootRef.current) return;
    const previouslyFocused = document.activeElement;
    const target = rootRef.current.querySelector("[data-autofocus]") ?? rootRef.current.querySelector(FOCUSABLE) ?? rootRef.current;
    target.focus({ preventScroll: true });
    return () => previouslyFocused?.focus?.({ preventScroll: true });
  }, [open]);
  const handleKeyDown = (event) => {
    if (event.key === "Escape" && onBackdropClick) {
      event.stopPropagation();
      onBackdropClick();
      return;
    }
    if (event.key === "Tab" && rootRef.current) {
      const focusables = Array.from(
        rootRef.current.querySelectorAll(FOCUSABLE)
      );
      if (focusables.length === 0) {
        event.preventDefault();
        rootRef.current.focus({ preventScroll: true });
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === rootRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };
  const gestureStartedOutside = useRef(false);
  const handleRootPointerDownCapture = (event) => {
    gestureStartedOutside.current = event.target instanceof HTMLElement && event.target.dataset.pagemodalBackdrop !== void 0;
  };
  const handleOutsideClick = (event) => {
    if (event.target === event.currentTarget && gestureStartedOutside.current) {
      onBackdropClick?.();
    }
  };
  if (!open) return null;
  const overlay = /* @__PURE__ */ React.createElement(
    "div",
    {
      ref: setRoot,
      role: "dialog",
      "aria-modal": "true",
      "aria-label": ariaLabel,
      tabIndex: -1,
      onKeyDown: handleKeyDown,
      onPointerDownCapture: handleRootPointerDownCapture,
      className: "absolute inset-0 z-[1000] outline-none"
    },
    /* @__PURE__ */ React.createElement(
      "div",
      {
        "aria-hidden": "true",
        className: "pointer-events-none absolute inset-0 bg-gray-500/75"
      }
    ),
    /* @__PURE__ */ React.createElement(
      "div",
      {
        "data-pagemodal-backdrop": "",
        className: "absolute inset-0 overflow-y-auto",
        onClick: handleOutsideClick
      },
      /* @__PURE__ */ React.createElement(
        "div",
        {
          "data-pagemodal-backdrop": "",
          onClick: handleOutsideClick,
          className: `flex min-h-full justify-center p-4 text-center ${align === "end" ? "items-end sm:items-center" : "items-center"}`
        },
        children
      )
    )
  );
  return container ? createPortal(overlay, container) : overlay;
}

// src/components/HTMLBlock.tsx
import React2, { useMemo } from "react";
var HTMLBlock = ({
  html,
  className
}) => {
  const cleanHtml = useMemo(
    () => ({
      __html: sanitizeHtml(html)
    }),
    [html]
  );
  return /* @__PURE__ */ React2.createElement(
    "div",
    {
      className: `prose ${className ? ` ${className}` : ""}`,
      dangerouslySetInnerHTML: cleanHtml
    }
  );
};
var HTMLBlock_default = HTMLBlock;

// src/components/Pages/QuestionCard.tsx
import React3 from "react";
function QuestionCard({ image, children, insetImage }) {
  return /* @__PURE__ */ React3.createElement("div", { className: "mt-card bg-mt-surface relative mt-4 w-full max-w-lg transform rounded-lg text-left shadow-xl sm:my-8" }, image && /* @__PURE__ */ React3.createElement("div", { className: "aspect-h-1 aspect-w-1 bg-mt-surface-2 relative overflow-hidden rounded-t group-hover:opacity-75" }, /* @__PURE__ */ React3.createElement(
    "img",
    {
      src: image,
      alt: "",
      className: "w-full object-cover object-center"
    }
  ), insetImage && /* @__PURE__ */ React3.createElement(
    "div",
    {
      className: "absolute [bottom:4%] [left:4%] [height:41.25%] [width:55%] rounded-lg border-2 border-white bg-black bg-contain bg-center bg-no-repeat shadow-xl",
      style: { backgroundImage: `url(${insetImage})` }
    }
  )), /* @__PURE__ */ React3.createElement("div", { className: "px-4 pt-5 pb-4" }, children));
}

// src/components/SkipDialog.tsx
import React4 from "react";
import { ForwardIcon } from "@heroicons/react/20/solid";
function SkipDialog({
  skipDialogOpen,
  setSkipDialogOpen,
  onSkip,
  title,
  text
}) {
  return /* @__PURE__ */ React4.createElement(
    PageModal,
    {
      open: skipDialogOpen,
      onBackdropClick: () => setSkipDialogOpen(false),
      ariaLabel: title,
      align: "end"
    },
    /* @__PURE__ */ React4.createElement("div", { className: "bg-mt-surface relative transform overflow-hidden rounded-lg px-4 pt-5 pb-4 text-left shadow-xl sm:my-8 sm:w-full sm:max-w-lg sm:p-6" }, /* @__PURE__ */ React4.createElement("div", { className: "sm:flex sm:items-start" }, /* @__PURE__ */ React4.createElement("div", { className: "bg-mt-primary-soft mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:size-10" }, /* @__PURE__ */ React4.createElement(
      ForwardIcon,
      {
        "aria-hidden": "true",
        className: "text-mt-primary h-6 w-6"
      }
    )), /* @__PURE__ */ React4.createElement("div", { className: "mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" }, /* @__PURE__ */ React4.createElement("h3", { className: "text-mt-ink text-base font-semibold" }, title), /* @__PURE__ */ React4.createElement("div", { className: "mt-2" }, /* @__PURE__ */ React4.createElement("p", { className: "text-mt-ink-muted text-sm" }, text)))), /* @__PURE__ */ React4.createElement("div", { className: "mt-5 sm:mt-4 sm:flex sm:flex-row-reverse" }, /* @__PURE__ */ React4.createElement(
      "button",
      {
        type: "button",
        onClick: () => onSkip(),
        className: "mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs sm:ml-3 sm:w-auto"
      },
      "Skip"
    ), /* @__PURE__ */ React4.createElement(
      "button",
      {
        type: "button",
        "data-autofocus": true,
        onClick: () => setSkipDialogOpen(false),
        className: "bg-mt-surface text-mt-ink ring-mt-border hover:bg-mt-surface-2 mt-3 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs ring-1 ring-inset sm:mt-0 sm:w-auto"
      },
      "Cancel"
    )))
  );
}

// src/components/BackgroundWrapper.tsx
import React5 from "react";
function BackgroundWrapper({
  children,
  backgroundImage
}) {
  return /* @__PURE__ */ React5.createElement(
    "div",
    {
      className: "flex h-full w-full flex-col items-center justify-center overflow-auto bg-cover bg-center bg-no-repeat p-4",
      style: backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : {}
    },
    children
  );
}

// src/utils/permissions.ts
var isBrowser = typeof window !== "undefined";
var isIOS = typeof window !== "undefined" && (/iP(hone|od|ad)/.test(navigator.userAgent) || navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent));
async function probeForEvent(eventName, timeoutMs = 800) {
  if (!isBrowser) return false;
  return new Promise((resolve) => {
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      window.removeEventListener(eventName, handler);
      resolve(value);
    };
    const timer = window.setTimeout(() => done(false), timeoutMs);
    const handler = (evt) => {
      if (eventName === "deviceorientation") {
        if (evt && (evt.alpha != null || evt.beta != null || evt.gamma != null)) {
          window.clearTimeout(timer);
          done(true);
        }
      } else {
        if (evt && (evt.acceleration != null || evt.accelerationIncludingGravity != null || evt.rotationRate != null)) {
          window.clearTimeout(timer);
          done(true);
        }
      }
    };
    window.addEventListener(eventName, handler, { passive: true });
  });
}
var PERMISSION_STORE_KEY = "MAGICAL_TRAILS_PERMISSION_STORE";
function safeParseStore(raw) {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
}
function loadPermissionStore() {
  if (!isBrowser) return {};
  try {
    return safeParseStore(window.localStorage.getItem(PERMISSION_STORE_KEY));
  } catch {
    return {};
  }
}
function savePermissionStore(store) {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(PERMISSION_STORE_KEY, JSON.stringify(store));
  } catch {
  }
}
function setUserDenied(id, userDenied) {
  const store = loadPermissionStore();
  store[id] = {
    ...store[id] ?? {},
    userDenied,
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  };
  savePermissionStore(store);
}
function getUserDenied(id) {
  const store = loadPermissionStore();
  return Boolean(store[id]?.userDenied);
}
function setLastKnownPermission(id, status) {
  const store = loadPermissionStore();
  store[id] = {
    ...store[id] ?? {},
    lastKnown: status,
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  };
  savePermissionStore(store);
}
function getLastKnownPermission(id) {
  const store = loadPermissionStore();
  const entry = store[id];
  return entry?.lastKnown ?? null;
}
function recordUserDeniedPermission(id) {
  setUserDenied(id, true);
  setLastKnownPermission(id, "user_denied");
}
function clearUserDeniedPermission(id) {
  setUserDenied(id, false);
}
async function queryPermission(name) {
  if (!isBrowser || !("permissions" in navigator)) return null;
  try {
    const status = await navigator.permissions.query({ name });
    const state = status.state || status.status;
    if (state === "granted") return "granted";
    if (state === "denied") return "denied";
    return "prompt";
  } catch {
    return null;
  }
}
async function getGeolocationPermissionStatus() {
  if (!isBrowser || !("geolocation" in navigator)) return "unavailable";
  const queried = await queryPermission("geolocation");
  if (queried) return queried;
  return "prompt";
}
async function getCameraPermissionStatus() {
  if (!isBrowser || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return "unavailable";
  }
  const queried = await queryPermission("camera");
  if (queried) return queried;
  return "prompt";
}
async function getMotionPermissionStatus() {
  if (!isBrowser) return "unavailable";
  const DeviceMotion = window.DeviceMotionEvent;
  const DeviceOrientation = window.DeviceOrientationEvent;
  if (!DeviceMotion && !DeviceOrientation) return "unavailable";
  const eventsAllowed = await probeForEvent("devicemotion", 400) || await probeForEvent("deviceorientation", 400);
  if (eventsAllowed) return "granted";
  if (isIOS) {
    if (typeof DeviceMotion?.requestPermission === "function" || typeof DeviceOrientation?.requestPermission === "function") {
      return "prompt";
    }
    return "blocked_by_system";
  }
  if (typeof DeviceMotion?.requestPermission === "function" || typeof DeviceOrientation?.requestPermission === "function") {
    return "prompt";
  }
  return "granted";
}
async function requestGeolocationPermission() {
  if (!isBrowser || !("geolocation" in navigator)) return "unavailable";
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve("granted"),
      async (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          const queried = await queryPermission("geolocation");
          if (isIOS && (queried === "prompt" || queried === null)) {
            resolve("blocked_by_system");
            return;
          }
          resolve(
            queried === "prompt" || queried === null ? "prompt" : "denied"
          );
          return;
        }
        if (err.code === err.POSITION_UNAVAILABLE || err.code === err.TIMEOUT) {
          resolve("granted_no_fix");
          return;
        }
        resolve("prompt");
      },
      {
        enableHighAccuracy: true,
        maximumAge: 1e4,
        timeout: 3e4
      }
    );
  });
}
async function requestCameraPermission() {
  if (!isBrowser || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return "unavailable";
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach((t) => t.stop());
    return "granted";
  } catch (err) {
    const name = err?.name;
    if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
      return "denied";
    }
    if (name === "NotFoundError") {
      return "unavailable";
    }
    return "prompt";
  }
}
async function requestMotionPermission() {
  if (!isBrowser) return "unavailable";
  const DeviceMotion = window.DeviceMotionEvent;
  const DeviceOrientation = window.DeviceOrientationEvent;
  if (!DeviceMotion && !DeviceOrientation) return "unavailable";
  const results = [];
  let eventsAllowed = false;
  try {
    if (typeof DeviceMotion?.requestPermission === "function") {
      results.push(await DeviceMotion.requestPermission());
    }
    if (typeof DeviceOrientation?.requestPermission === "function") {
      results.push(await DeviceOrientation.requestPermission());
    }
  } catch {
    return "denied";
  }
  eventsAllowed = await probeForEvent("devicemotion", 600) || await probeForEvent("deviceorientation", 600);
  if (results.length > 0) {
    if (results.every((r) => r === "granted")) {
      return eventsAllowed ? "granted" : "blocked_by_system";
    }
    return "denied";
  }
  if (isIOS) {
    return eventsAllowed ? "granted" : "blocked_by_system";
  }
  return "granted";
}
async function getPermissionStatus(id) {
  let status;
  switch (id) {
    case "geolocation":
      status = await getGeolocationPermissionStatus();
      break;
    case "camera":
      status = await getCameraPermissionStatus();
      break;
    case "motion":
      status = await getMotionPermissionStatus();
      break;
  }
  if (status === "prompt" && getUserDenied(id)) {
    return "user_denied";
  }
  return status;
}
async function requestPermission(id) {
  let status;
  switch (id) {
    case "geolocation":
      status = await requestGeolocationPermission();
      break;
    case "camera":
      status = await requestCameraPermission();
      break;
    case "motion":
      status = await requestMotionPermission();
      break;
  }
  if (status === "granted") {
    clearUserDeniedPermission(id);
  }
  setLastKnownPermission(id, status);
  return status;
}

// src/components/PermissionsDialog.tsx
import React6, { useEffect as useEffect2, useRef as useRef2 } from "react";
import { ShieldCheckIcon } from "@heroicons/react/20/solid";
function PermissionsDialog({
  open,
  setOpen,
  controller,
  onComplete,
  title,
  description
}) {
  const {
    requiredPermissions,
    statuses,
    loading,
    requesting,
    requestPermission: requestPermission2,
    denyInApp,
    allGranted,
    refresh
  } = controller;
  const onCompleteCalledRef = useRef2(false);
  useEffect2(() => {
    if (!open) return;
    onCompleteCalledRef.current = false;
    refresh();
  }, [open, refresh]);
  useEffect2(() => {
    if (open && allGranted && onComplete && !onCompleteCalledRef.current) {
      onCompleteCalledRef.current = true;
      onComplete();
    }
  }, [allGranted, onComplete, open]);
  const handleRequest = async (id) => {
    await requestPermission2(id);
  };
  const labels = {
    geolocation: "Location",
    camera: "Camera",
    motion: "Motion / Gyroscope"
  };
  const descriptions = {
    geolocation: "Used to show your position on the trail map and unlock nearby points.",
    camera: "Used for AR-style games and scanning trail markers.",
    motion: "Used on iOS to stabilise AR content when you move your device."
  };
  const deniedHelpText = {
    geolocation: "Location access is blocked in your browser. To re-enable it, open your browser's site settings for Magical Trails and set Location to \u201CAllow\u201D, then refresh the page.",
    camera: "Camera access is blocked in your browser. To re-enable it, open your browser's site settings for Magical Trails and set Camera to \u201CAllow\u201D, then refresh the page.",
    motion: "Motion access is blocked on this device. On iOS, go to Settings \u2192 Safari \u2192 Motion & Orientation Access, and turn it on, then reload the trail."
  };
  const blockedText = {
    geolocation: isIOS ? "Location is blocked by iOS settings. Go to Settings \u2192 Privacy & Security \u2192 Location Services \u2192 Safari Websites \u2192 set to \u201CWhile Using the App\u201D, then return to Safari and reload this page." : "Location access is blocked by your device settings. Open your browser's site settings for Magical Trails and set Location to \u201CAllow\u201D, then refresh the page.",
    camera: "Camera access is blocked by your device settings. Open your browser's site settings for Magical Trails and set Camera to \u201CAllow\u201D, then refresh the page.",
    motion: "Motion is blocked by your device settings. On iOS, go to Settings \u2192 Safari \u2192 Motion & Orientation Access, and turn it on, then reload the trail."
  };
  const unavailableText = {
    geolocation: "Location is not available in this browser.",
    camera: "Camera is not available in this browser.",
    motion: "Motion sensors are not available on this device / browser."
  };
  const renderStatusBadge = (status) => {
    const badgeBase = "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset";
    switch (status) {
      case "granted":
        return /* @__PURE__ */ React6.createElement(
          "span",
          {
            className: `${badgeBase} bg-green-50 text-green-700 ring-green-600/20`
          },
          "Granted"
        );
      case "prompt":
        return /* @__PURE__ */ React6.createElement(
          "span",
          {
            className: `${badgeBase} bg-mt-surface-2 text-mt-ink-soft ring-gray-500/20`
          },
          "Not yet requested"
        );
      case "user_denied":
        return /* @__PURE__ */ React6.createElement(
          "span",
          {
            className: `${badgeBase} bg-amber-50 text-amber-700 ring-amber-600/20`
          },
          "Not now"
        );
      case "denied":
        return /* @__PURE__ */ React6.createElement(
          "span",
          {
            className: `${badgeBase} text-mt-danger bg-red-50 ring-red-600/20`
          },
          "Blocked"
        );
      case "unavailable":
        return /* @__PURE__ */ React6.createElement(
          "span",
          {
            className: `${badgeBase} bg-amber-50 text-amber-700 ring-amber-600/20`
          },
          "Unavailable"
        );
      case "blocked_by_system":
        return /* @__PURE__ */ React6.createElement(
          "span",
          {
            className: `${badgeBase} bg-amber-50 text-amber-700 ring-amber-600/20`
          },
          "Blocked by settings"
        );
      case "granted_no_fix":
        return /* @__PURE__ */ React6.createElement(
          "span",
          {
            className: `${badgeBase} bg-amber-50 text-amber-700 ring-amber-600/20`
          },
          "Granted (no fix)"
        );
    }
  };
  const renderAction = (id, status) => {
    if (status === "prompt" || status === "user_denied") {
      return /* @__PURE__ */ React6.createElement("div", { className: "flex items-center gap-2" }, /* @__PURE__ */ React6.createElement(
        "button",
        {
          type: "button",
          onClick: () => handleRequest(id),
          disabled: requesting === id,
          className: "border-mt-border text-mt-ink hover:bg-mt-surface-2 disabled:bg-mt-surface-2 inline-flex items-center justify-center rounded-md border px-3 py-1.5 text-sm font-medium shadow-sm transition disabled:cursor-not-allowed"
        },
        requesting === id ? "Requesting\u2026" : "Enable"
      ), status === "prompt" && /* @__PURE__ */ React6.createElement(
        "button",
        {
          type: "button",
          onClick: () => denyInApp(id),
          className: "border-mt-border-soft text-mt-ink-muted hover:bg-mt-surface-2 inline-flex items-center justify-center rounded-md border px-3 py-1.5 text-sm font-medium shadow-sm transition"
        },
        "Not now"
      ));
    }
    return null;
  };
  const handleClose = (nextOpen) => {
    setOpen(nextOpen);
  };
  return /* @__PURE__ */ React6.createElement(
    PageModal,
    {
      open,
      onBackdropClick: () => handleClose(false),
      ariaLabel: title || "Allow permissions to continue",
      align: "end"
    },
    /* @__PURE__ */ React6.createElement("div", { className: "bg-mt-surface relative transform overflow-hidden rounded-lg px-4 pt-5 pb-4 text-left shadow-xl sm:my-8 sm:w-full sm:max-w-lg sm:p-6" }, /* @__PURE__ */ React6.createElement("div", { className: "sm:flex sm:items-start" }, /* @__PURE__ */ React6.createElement("div", { className: "bg-mt-primary-soft mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:size-10" }, /* @__PURE__ */ React6.createElement(
      ShieldCheckIcon,
      {
        "aria-hidden": "true",
        className: "text-mt-primary h-6 w-6"
      }
    )), /* @__PURE__ */ React6.createElement("div", { className: "mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" }, /* @__PURE__ */ React6.createElement("h3", { className: "text-mt-ink text-base font-semibold" }, title || "Allow permissions to continue"), /* @__PURE__ */ React6.createElement("div", { className: "mt-2" }, /* @__PURE__ */ React6.createElement("p", { className: "text-mt-ink-muted text-sm" }, description || "We use these permissions to unlock trail features on your device.")))), /* @__PURE__ */ React6.createElement("div", { className: "mt-4" }, loading ? /* @__PURE__ */ React6.createElement("p", { className: "text-mt-ink-muted text-sm" }, "Checking permissions\u2026") : /* @__PURE__ */ React6.createElement("ul", { className: "divide-y divide-gray-200" }, requiredPermissions.map((id) => {
      const status = statuses[id];
      return /* @__PURE__ */ React6.createElement("li", { key: id, className: "py-3" }, /* @__PURE__ */ React6.createElement("div", { className: "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" }, /* @__PURE__ */ React6.createElement("div", { className: "space-y-1" }, /* @__PURE__ */ React6.createElement("p", { className: "text-mt-ink text-sm font-semibold" }, labels[id]), /* @__PURE__ */ React6.createElement("p", { className: "text-mt-ink-muted text-sm" }, descriptions[id])), /* @__PURE__ */ React6.createElement("div", { className: "flex items-center gap-3 sm:flex-shrink-0" }, renderStatusBadge(status), renderAction(id, status))), (status === "denied" || status === "unavailable" || status === "blocked_by_system") && /* @__PURE__ */ React6.createElement("p", { className: "text-mt-danger mt-2 text-xs" }, status === "denied" ? deniedHelpText[id] : status === "blocked_by_system" ? blockedText[id] : unavailableText[id]));
    }))), /* @__PURE__ */ React6.createElement("div", { className: "mt-5 sm:mt-4 sm:flex sm:flex-row-reverse" }, /* @__PURE__ */ React6.createElement(
      "button",
      {
        type: "button",
        onClick: () => {
          if (allGranted && onComplete && !onCompleteCalledRef.current) {
            onCompleteCalledRef.current = true;
            onComplete();
          }
        },
        disabled: !allGranted,
        className: "mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs disabled:cursor-not-allowed disabled:bg-indigo-300 sm:ml-3 sm:w-auto"
      },
      "Continue"
    ), /* @__PURE__ */ React6.createElement(
      "button",
      {
        type: "button",
        "data-autofocus": true,
        onClick: () => handleClose(false),
        className: "bg-mt-surface text-mt-ink ring-mt-border hover:bg-mt-surface-2 mt-3 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs ring-1 ring-inset sm:mt-0 sm:w-auto"
      },
      "Cancel"
    )))
  );
}

// src/components/Pages/arcade/GameCanvasGuard.tsx
import React7, { useEffect as useEffect3, useRef as useRef3, useState } from "react";
var GameErrorState = ({
  onRetry,
  emoji = "\u{1F3AE}",
  chunkError = false,
  transparent = false
}) => /* @__PURE__ */ React7.createElement(
  "div",
  {
    role: "alert",
    className: `flex h-full w-full flex-col items-center justify-center p-6 text-center ${transparent ? "bg-black/75" : "bg-black"}`
  },
  /* @__PURE__ */ React7.createElement("div", { className: "mb-2 text-3xl", "aria-hidden": "true" }, emoji),
  /* @__PURE__ */ React7.createElement("p", { className: "text-sm font-semibold text-white" }, "The game hit a snag"),
  /* @__PURE__ */ React7.createElement("p", { className: "mt-1 max-w-xs text-xs text-white/70" }, chunkError ? "Part of the game failed to download. Check your connection, then reload." : "The 3D view could not keep running. Closing other tabs or games can help."),
  /* @__PURE__ */ React7.createElement(
    "button",
    {
      type: "button",
      onClick: chunkError && typeof window !== "undefined" ? () => window.location.reload() : onRetry,
      className: "mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover focus-visible:outline-mt-primary mt-4 inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold shadow-xs focus-visible:outline focus-visible:outline-offset-2"
    },
    chunkError ? "Reload page" : "Try again"
  )
);
var CHUNK_ERROR_RE = /loading chunk|dynamically imported module|importing a module script|module script failed|failed to fetch|import\(\) failed|NetworkError|error loading/i;
var CRASH_RELOAD_AFTER = 2;
var CRASH_WINDOW_MS = 6e4;
var crashLog = /* @__PURE__ */ new Map();
function recordCrash(logTag, now = Date.now()) {
  const recent = (crashLog.get(logTag) ?? []).filter(
    (t) => now - t < CRASH_WINDOW_MS
  );
  recent.push(now);
  crashLog.set(logTag, recent);
  return recent.length >= CRASH_RELOAD_AFTER;
}
var GameErrorBoundary = class extends React7.Component {
  state = { hasError: false, chunkError: false };
  static getDerivedStateFromError(error) {
    const message = error instanceof Error ? error.message : String(error);
    return { hasError: true, chunkError: CHUNK_ERROR_RE.test(message) };
  }
  componentDidCatch(error) {
    console.error(`[${this.props.logTag}] game crashed`, error);
    reportCaughtError({
      source: this.props.logTag,
      kind: "game-crash",
      error
    });
    if (recordCrash(this.props.logTag) && !this.state.chunkError) {
      this.setState({ chunkError: true });
    }
  }
  render() {
    if (this.state.hasError) {
      return /* @__PURE__ */ React7.createElement(
        GameErrorState,
        {
          onRetry: this.props.onRetry,
          emoji: this.props.emoji,
          chunkError: this.state.chunkError,
          transparent: this.props.transparent
        }
      );
    }
    return this.props.children;
  }
};
var AUTO_RETRY_DELAY_MS = 350;
var AUTO_RETRY_BUDGET = 2;
var AUTO_RETRY_WINDOW_MS = 3e4;
var autoRetryLog = /* @__PURE__ */ new Map();
function takeAutoRetry(logTag, now = Date.now()) {
  const recent = (autoRetryLog.get(logTag) ?? []).filter(
    (t) => now - t < AUTO_RETRY_WINDOW_MS
  );
  if (recent.length >= AUTO_RETRY_BUDGET) {
    autoRetryLog.set(logTag, recent);
    return false;
  }
  recent.push(now);
  autoRetryLog.set(logTag, recent);
  return true;
}
function GameCanvasGuard({
  onRetry,
  loadingLabel = "Loading game\u2026",
  logTag = "GameCanvasGuard",
  emoji,
  transparent = false,
  children
}) {
  const containerRef = useRef3(null);
  const [contextLost, setContextLost] = useState(false);
  const onRetryRef = useRef3(onRetry);
  onRetryRef.current = onRetry;
  useEffect3(() => {
    const el = containerRef.current;
    if (!el) return;
    let retryTimer = null;
    let onVisible = null;
    const clearAutoRetry = () => {
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = null;
      if (onVisible)
        document.removeEventListener("visibilitychange", onVisible);
      onVisible = null;
    };
    const autoRetry = () => {
      clearAutoRetry();
      console.info(`[${logTag}] WebGL context lost \u2014 auto-remounting`);
      onRetryRef.current();
    };
    const handleContextLost = (event) => {
      event.preventDefault();
      console.error(`[${logTag}] WebGL context lost`);
      reportCaughtError({ source: logTag, kind: "webgl-context-lost" });
      setContextLost(true);
      if (!takeAutoRetry(logTag)) return;
      if (document.visibilityState === "visible") {
        retryTimer = setTimeout(autoRetry, AUTO_RETRY_DELAY_MS);
        return;
      }
      onVisible = () => {
        if (document.visibilityState === "visible") autoRetry();
      };
      document.addEventListener("visibilitychange", onVisible);
    };
    const handleContextRestored = () => {
      clearAutoRetry();
      console.info(`[${logTag}] WebGL context restored \u2014 remounting`);
      onRetryRef.current();
    };
    el.addEventListener("webglcontextlost", handleContextLost, true);
    el.addEventListener("webglcontextrestored", handleContextRestored, true);
    return () => {
      clearAutoRetry();
      el.removeEventListener("webglcontextlost", handleContextLost, true);
      el.removeEventListener(
        "webglcontextrestored",
        handleContextRestored,
        true
      );
    };
  }, [logTag]);
  return /* @__PURE__ */ React7.createElement(
    "div",
    {
      ref: containerRef,
      className: `relative h-full w-full overflow-hidden ${transparent ? "" : "bg-black"}`
    },
    /* @__PURE__ */ React7.createElement(
      GameErrorBoundary,
      {
        onRetry,
        emoji,
        logTag,
        transparent
      },
      /* @__PURE__ */ React7.createElement(
        React7.Suspense,
        {
          fallback: /* @__PURE__ */ React7.createElement(
            "div",
            {
              role: "status",
              "aria-live": "polite",
              className: `flex h-full w-full items-center justify-center text-sm font-medium text-white ${transparent ? "" : "bg-black"}`
            },
            loadingLabel
          )
        },
        children
      )
    ),
    contextLost && /* @__PURE__ */ React7.createElement("div", { className: "absolute inset-0 z-10" }, /* @__PURE__ */ React7.createElement(
      GameErrorState,
      {
        onRetry,
        emoji,
        transparent
      }
    ))
  );
}

// src/utils/usePermissionStatuses.ts
import { useCallback as useCallback2, useEffect as useEffect4, useMemo as useMemo2, useRef as useRef4, useState as useState2 } from "react";
function createInitialMap(required) {
  return required.reduce((acc, id) => {
    acc[id] = getLastKnownPermission(id) ?? "prompt";
    return acc;
  }, {});
}
function usePermissionStatuses(requiredPermissions) {
  const normalized = useMemo2(
    () => Array.from(new Set(requiredPermissions)).sort(),
    // Use string key to avoid reruns when caller passes a new array instance.
    [requiredPermissions.join("|")]
  );
  const key = useMemo2(() => normalized.join("|") || "none", [normalized]);
  const [statuses, setStatuses] = useState2(
    () => createInitialMap(normalized)
  );
  const [loading, setLoading] = useState2(false);
  const [hasLoaded, setHasLoaded] = useState2(false);
  const [requesting, setRequesting] = useState2(null);
  const isMountedRef = useRef4(true);
  useEffect4(() => {
    setStatuses(createInitialMap(normalized));
    setHasLoaded(false);
  }, [key, normalized]);
  useEffect4(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const refresh = useCallback2(async () => {
    setLoading(true);
    try {
      const next = {};
      for (const id of normalized) {
        const status = await getPermissionStatus(id);
        const lastKnown = getLastKnownPermission(id);
        const shouldUseMemory = status === "prompt" && isIOS && (id === "camera" || id === "motion" || id === "geolocation") && (lastKnown === "granted" || lastKnown === "granted_no_fix" || lastKnown === "denied" || lastKnown === "user_denied");
        const withMemory = shouldUseMemory && lastKnown ? lastKnown : status;
        next[id] = withMemory;
        if (withMemory !== "prompt") {
          setLastKnownPermission(id, withMemory);
        }
      }
      if (!isMountedRef.current) return next;
      setStatuses((prev) => ({ ...prev, ...next }));
      return next;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setHasLoaded(true);
      }
    }
  }, [normalized]);
  const refreshAndGet = useCallback2(async () => {
    const updated = await refresh();
    return updated ?? statuses;
  }, [refresh, statuses]);
  const requestPermission2 = useCallback2(async (id) => {
    setRequesting(id);
    try {
      const status = await requestPermission(id);
      setStatuses((prev) => ({ ...prev, [id]: status }));
      setLastKnownPermission(id, status);
      if (status !== "user_denied") {
        clearUserDeniedPermission(id);
      }
      return status;
    } finally {
      setRequesting((current) => current === id ? null : current);
    }
  }, []);
  const denyInApp = useCallback2((id) => {
    recordUserDeniedPermission(id);
    setStatuses((prev) => ({ ...prev, [id]: "user_denied" }));
    setLastKnownPermission(id, "user_denied");
  }, []);
  const clearInAppDeny = useCallback2((id) => {
    clearUserDeniedPermission(id);
    setLastKnownPermission(id, "prompt");
    setStatuses((prev) => ({ ...prev, [id]: "prompt" }));
  }, []);
  const derived = useMemo2(() => {
    const values = normalized.map((id) => statuses[id]);
    return {
      allGranted: values.length > 0 && values.every((s) => s === "granted" || s === "granted_no_fix"),
      anyPrompt: values.some((s) => s === "prompt"),
      anyUserDenied: values.some((s) => s === "user_denied"),
      anyDenied: values.some((s) => s === "denied" || s === "blocked_by_system"),
      anyUnavailable: values.some((s) => s === "unavailable")
    };
  }, [key, normalized, statuses]);
  return {
    requiredPermissions: normalized,
    statuses,
    loading,
    hasLoaded,
    requesting,
    refresh,
    refreshAndGet,
    requestPermission: requestPermission2,
    denyInApp,
    clearInAppDeny,
    ...derived
  };
}

// src/components/Pages/arShared/ARPageShell.tsx
import React8, {
  useCallback as useCallback3,
  useEffect as useEffect5,
  useMemo as useMemo3,
  useRef as useRef5,
  useState as useState3
} from "react";
import { CheckIcon, SparklesIcon } from "@heroicons/react/20/solid";
function ARPageShell({
  page,
  copy,
  eventPrefix,
  trailEvent,
  nextPage,
  editorMode = false,
  warmUp,
  cameraOnly = false,
  renderView,
  completeExtra,
  autoAdvanceOnComplete = false,
  renderComplete
}) {
  const {
    backgroundImage,
    title,
    instructions,
    instructionsImage,
    completeImage,
    completeTitle,
    completeText
  } = page;
  const StartIcon = copy.startIcon ?? SparklesIcon;
  const [step, setStep] = useState3("INSTRUCTIONS");
  const warmUpRef = useRef5(warmUp);
  useEffect5(() => {
    warmUpRef.current = warmUp;
  }, [warmUp]);
  useEffect5(() => {
    warmUpRef.current?.();
    if (!cameraOnly) warmUpImmersiveARSupport();
  }, [cameraOnly]);
  const [sceneKey, setSceneKey] = useState3(0);
  const [noCamera, setNoCamera] = useState3(false);
  const [skipDialogOpen, setSkipDialogOpen] = useState3(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState3(false);
  const [requiredPermissions, setRequiredPermissions] = useState3(["camera"]);
  const [permissionsResolved, setPermissionsResolved] = useState3(false);
  const wasHidden = useRef5(false);
  const foundFiredRef = useRef5(false);
  useEffect5(() => {
    let cancelled = false;
    void (async () => {
      if (!isIOS || cameraOnly) {
        if (!cancelled) {
          setRequiredPermissions(["camera"]);
          setPermissionsResolved(true);
        }
        return;
      }
      const motionStatus = await getPermissionStatus("motion");
      if (cancelled) return;
      setRequiredPermissions(
        motionStatus !== "unavailable" ? ["camera", "motion"] : ["camera"]
      );
      setPermissionsResolved(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [cameraOnly]);
  const perms = usePermissionStatuses(requiredPermissions);
  const {
    statuses,
    loading: permissionsLoading,
    hasLoaded,
    refresh,
    refreshAndGet,
    allGranted,
    anyDenied,
    anyUnavailable,
    anyPrompt,
    anyUserDenied
  } = perms;
  useEffect5(() => {
    if (step === "INSTRUCTIONS") refresh();
  }, [refresh, step, requiredPermissions]);
  const overallState = useMemo3(() => {
    if (anyUnavailable) return "unavailable";
    if (anyDenied) return "denied";
    if (anyUserDenied) return "user_denied";
    if (anyPrompt) return "prompt";
    return allGranted ? "granted" : "prompt";
  }, [allGranted, anyDenied, anyPrompt, anyUnavailable, anyUserDenied]);
  const motionBlocked = requiredPermissions.includes("motion") && statuses.motion === "blocked_by_system";
  const startView = useCallback3(() => {
    foundFiredRef.current = false;
    setNoCamera(false);
    setStep("VIEW");
    trailEvent(`${eventPrefix}: Started`);
  }, [eventPrefix, trailEvent]);
  const startWithoutCamera = useCallback3(() => {
    foundFiredRef.current = false;
    setNoCamera(true);
    setStep("VIEW");
    trailEvent(`${eventPrefix}: Started`);
    trailEvent(`${eventPrefix}: Started without camera`);
  }, [eventPrefix, trailEvent]);
  const handleFound = useCallback3(() => {
    if (foundFiredRef.current) return;
    foundFiredRef.current = true;
    trailEvent(`${eventPrefix}: Found`);
  }, [eventPrefix, trailEvent]);
  const handleComplete = useCallback3(() => {
    trailEvent(`${eventPrefix}: Completed`);
    if (autoAdvanceOnComplete) {
      nextPage && nextPage();
      return;
    }
    setStep("COMPLETE");
  }, [autoAdvanceOnComplete, eventPrefix, nextPage, trailEvent]);
  const lookAgain = useCallback3(() => {
    setSceneKey((k) => k + 1);
    setStep("VIEW");
  }, []);
  const continueTrail = useCallback3(() => {
    nextPage && nextPage();
  }, [nextPage]);
  useEffect5(() => {
    if (step !== "VIEW" || editorMode) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        wasHidden.current = true;
        return;
      }
      if (wasHidden.current && document.visibilityState === "visible") {
        wasHidden.current = false;
        setStep("INSTRUCTIONS");
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [editorMode, step]);
  const primaryButtonClass = "shadow-xs inline-flex w-full items-center justify-center rounded-md mt-btn bg-mt-primary px-3.5 py-2.5 text-lg font-semibold text-mt-on-primary hover:bg-mt-primary-hover focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-mt-primary";
  const canStartDirectly = editorMode || permissionsResolved && overallState === "granted";
  const motionSuffix = requiredPermissions.includes("motion") ? " and motion" : "";
  const offerNoCamera = !!copy.noCameraLabel && !editorMode && permissionsResolved && hasLoaded && overallState !== "granted";
  return /* @__PURE__ */ React8.createElement(React8.Fragment, null, step === "INSTRUCTIONS" && /* @__PURE__ */ React8.createElement(BackgroundWrapper, { backgroundImage }, /* @__PURE__ */ React8.createElement(QuestionCard, { image: instructionsImage }, title && /* @__PURE__ */ React8.createElement("h2", { className: "text-mt-ink mb-2 text-center text-xl font-bold" }, title), instructions && /* @__PURE__ */ React8.createElement(HTMLBlock_default, { html: instructions }), !editorMode && (!permissionsResolved || permissionsLoading || !hasLoaded) && /* @__PURE__ */ React8.createElement("p", { className: "text-mt-ink-muted text-sm" }, "Checking permissions\u2026"), !editorMode && permissionsResolved && !permissionsLoading && hasLoaded && /* @__PURE__ */ React8.createElement("div", { className: "text-mt-ink-soft space-y-2 text-sm" }, overallState === "prompt" && /* @__PURE__ */ React8.createElement("p", null, "We need access to your camera", motionSuffix, " ", copy.permissionPurpose, "."), overallState === "user_denied" && /* @__PURE__ */ React8.createElement("p", null, "You chose not to enable the camera", motionSuffix, " yet. Enable it ", copy.permissionPurpose, "."), overallState === "denied" && /* @__PURE__ */ React8.createElement("p", null, motionBlocked ? `Motion access is blocked by your device settings. Enable Motion & Orientation Access ${copy.motionPurpose ?? copy.permissionPurpose}.` : `Camera access is blocked. Enable it ${copy.permissionPurpose}.`), overallState === "unavailable" && /* @__PURE__ */ React8.createElement("p", null, "Camera is not available on this device.")), /* @__PURE__ */ React8.createElement("div", { className: "mt-5 flex flex-col gap-3" }, canStartDirectly && /* @__PURE__ */ React8.createElement("button", { className: primaryButtonClass, onClick: startView }, /* @__PURE__ */ React8.createElement(StartIcon, { "aria-hidden": "true", className: "mr-2 h-6 w-6" }), copy.startLabel), !editorMode && permissionsResolved && overallState !== "granted" && hasLoaded && /* @__PURE__ */ React8.createElement(
    "button",
    {
      className: primaryButtonClass,
      onClick: () => setPermissionsDialogOpen(true)
    },
    "Enable camera",
    requiredPermissions.includes("motion") ? " & motion" : ""
  ), offerNoCamera && /* @__PURE__ */ React8.createElement(
    "button",
    {
      type: "button",
      onClick: startWithoutCamera,
      className: "bg-mt-surface text-mt-ink ring-mt-border hover:bg-mt-surface-2 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs ring-1 ring-inset"
    },
    copy.noCameraLabel
  ), /* @__PURE__ */ React8.createElement(
    "button",
    {
      type: "button",
      onClick: () => setSkipDialogOpen(true),
      className: "bg-mt-surface text-mt-ink ring-mt-border hover:bg-mt-surface-2 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs ring-1 ring-inset"
    },
    "Skip"
  )))), step === "VIEW" && // Procedural geometry over a camera feed — the guard covers WebGL
  // context loss, a failed lazy-chunk load, or a scene throw. Keyed so
  // a retry (or 'Look again') gets a fresh canvas.
  /* @__PURE__ */ React8.createElement("div", { className: "relative h-full w-full" }, /* @__PURE__ */ React8.createElement(
    GameCanvasGuard,
    {
      key: sceneKey,
      logTag: copy.logTag,
      emoji: copy.emoji,
      loadingLabel: copy.loadingLabel,
      onRetry: () => setSceneKey((k) => k + 1)
    },
    renderView({
      onFound: handleFound,
      onComplete: handleComplete,
      onSkip: () => setSkipDialogOpen(true),
      sceneKey,
      noCamera
    })
  ), /* @__PURE__ */ React8.createElement(
    "button",
    {
      type: "button",
      onClick: () => setSkipDialogOpen(true),
      className: "absolute top-3 right-3 z-40 rounded-full bg-black/55 px-3.5 py-1.5 text-sm font-semibold text-white shadow-lg hover:bg-black/70 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-white"
    },
    "Skip"
  )), step === "COMPLETE" && /* @__PURE__ */ React8.createElement(BackgroundWrapper, { backgroundImage }, /* @__PURE__ */ React8.createElement(QuestionCard, { image: completeImage }, renderComplete ? renderComplete({ lookAgain, continueTrail }) : /* @__PURE__ */ React8.createElement(React8.Fragment, null, /* @__PURE__ */ React8.createElement("div", { className: "bg-mt-success-soft mx-auto flex h-12 w-12 items-center justify-center rounded-full" }, /* @__PURE__ */ React8.createElement(
    CheckIcon,
    {
      className: "text-mt-success h-6 w-6",
      "aria-hidden": "true"
    }
  )), /* @__PURE__ */ React8.createElement("div", { className: "mt-3 text-center sm:mt-5" }, /* @__PURE__ */ React8.createElement("h3", { className: "text-mt-ink text-base leading-6 font-semibold" }, completeTitle || copy.completeTitleFallback), completeText && /* @__PURE__ */ React8.createElement("div", { className: "mt-2" }, /* @__PURE__ */ React8.createElement(
    HTMLBlock_default,
    {
      className: "text-mt-ink-muted text-sm",
      html: completeText
    }
  )), completeExtra, /* @__PURE__ */ React8.createElement("div", { className: "mt-5 flex flex-col gap-3" }, /* @__PURE__ */ React8.createElement(
    "button",
    {
      onClick: continueTrail,
      className: "mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover focus-visible:outline-mt-primary mt-3 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2"
    },
    "Continue"
  ), copy.lookAgainLabel && /* @__PURE__ */ React8.createElement(
    "button",
    {
      type: "button",
      onClick: lookAgain,
      className: "bg-mt-surface text-mt-ink ring-mt-border hover:bg-mt-surface-2 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs ring-1 ring-inset"
    },
    copy.lookAgainLabel
  )))))), /* @__PURE__ */ React8.createElement(
    SkipDialog,
    {
      skipDialogOpen,
      setSkipDialogOpen,
      title: "Skip",
      text: copy.skipText,
      onSkip: () => {
        setSkipDialogOpen(false);
        trailEvent(`${eventPrefix}: Skipped`);
        nextPage && nextPage();
      }
    }
  ), /* @__PURE__ */ React8.createElement(
    PermissionsDialog,
    {
      open: permissionsDialogOpen,
      setOpen: setPermissionsDialogOpen,
      controller: perms,
      title: "Allow camera to continue",
      description: copy.permissionDescription,
      onComplete: async () => {
        setPermissionsDialogOpen(false);
        const updated = await refreshAndGet();
        const granted = requiredPermissions.every(
          (id) => updated[id] === "granted"
        );
        if (granted) startView();
      }
    }
  ));
}

export {
  PageModalContainerProvider,
  PageModal,
  sanitizeHtml,
  HTMLBlock_default,
  QuestionCard,
  SkipDialog,
  BackgroundWrapper,
  isIOS,
  getPermissionStatus,
  PermissionsDialog,
  GameCanvasGuard,
  usePermissionStatuses,
  ARPageShell
};
//# sourceMappingURL=chunk-FXF7X2MJ.mjs.map